import React, { useRef } from 'react';
import { mergeAttributes } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { withMediaToken } from '../../utils/media';

/**
 * 본문 인라인 이미지 노드 (크기 조절 가능).
 *
 * 저장되는 `src` 는 토큰 없는 상대 경로를 유지한다. 만료되는 `?token=` 을 본문에
 * 굳히면 깨진 이미지가 되므로 토큰은 **화면에 그릴 때만** withMediaToken 으로 붙인다.
 *
 * 크기는 `src` 의 `?w=` 쿼리에 픽셀 너비로 싣는다 (예: `/v0.1/.../file?w=320`).
 * 이렇게 하면 저장 포맷이 표준 마크다운 이미지(`![](url)`) 그대로라 tiptap-markdown
 * 직렬화/파싱과 충돌이 없고, 백엔드 파일 핸들러는 여분의 `w` 쿼리를 무시한다.
 * w 가 없으면 원본 크기로 렌더(과거 이미지 호환).
 */

/**
 * 동영상 첨부 여부 — 업로드 시 src 에 붙인 `?kind=video` 마커로 판별한다.
 * 본문 저장 포맷은 이미지와 동일한 마크다운(`![](src)`)이라, 노드는 그대로
 * image 노드로 round-trip 되고 렌더만 <video> 로 분기한다.
 */
function isVideoSrc(src?: string | null): boolean {
  return /[?&]kind=video(?:&|$)/.test(src ?? '');
}

/** src 쿼리의 w(px)를 읽는다. 없으면 null. */
function parseWidth(src?: string | null): number | null {
  const m = /[?&]w=(\d+)/.exec(src ?? '');
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/** src 에서 기존 w 쿼리를 제거. */
function stripWidth(src: string): string {
  return src
    .replace(/([?&])w=\d+/, (_full, sep: string) => (sep === '?' ? '?' : ''))
    .replace(/\?$/, '')
    .replace(/\?&/, '?');
}

/** src 에 w=px 쿼리를 설정(기존 w 는 교체). */
function withWidth(src: string, w: number): string {
  const base = stripWidth(src);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}w=${w}`;
}

const MIN_WIDTH = 60;

const ResizableImageView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  editor,
}) => {
  const src = (node.attrs.src as string) ?? '';
  const alt = (node.attrs.alt as string) ?? '';
  const width = parseWidth(src);
  const isVideo = isVideoSrc(src);
  const imgRef = useRef<HTMLImageElement & HTMLVideoElement>(null);
  const editable = editor.isEditable;

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = imgRef.current?.offsetWidth ?? width ?? 0;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(MIN_WIDTH, Math.round(startW + (ev.clientX - startX)));
      updateAttributes({ src: withWidth(src, next) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <NodeViewWrapper
      as="div"
      style={{ position: 'relative', display: 'inline-block', lineHeight: 0, maxWidth: '100%' }}
    >
      {isVideo ? (
        <video
          ref={imgRef}
          src={withMediaToken(src)}
          controls
          preload="metadata"
          style={{
            width: width ? `${width}px` : undefined,
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 4,
            display: 'block',
            outline: selected ? '2px solid #1976d2' : 'none',
          }}
          draggable={false}
        />
      ) : (
        <img
          ref={imgRef}
          src={withMediaToken(src)}
          alt={alt}
          style={{
            width: width ? `${width}px` : undefined,
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 4,
            display: 'block',
            outline: selected ? '2px solid #1976d2' : 'none',
          }}
          draggable={false}
        />
      )}
      {editable && (
        <span
          role="presentation"
          onMouseDown={startResize}
          title="드래그하여 크기 조절"
          style={{
            position: 'absolute',
            right: -6,
            bottom: -6,
            width: 13,
            height: 13,
            borderRadius: 3,
            background: '#1976d2',
            border: '2px solid #fff',
            cursor: 'nwse-resize',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
          }}
        />
      )}
    </NodeViewWrapper>
  );
};

export const MediaImage = Image.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },

  /** HTML 내보내기/복사용. 화면 src 에는 토큰을 붙인다(마크다운은 attrs.src 사용). */
  renderHTML({ HTMLAttributes }) {
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);
    let video = false;
    if (typeof attrs.src === 'string') {
      const w = parseWidth(attrs.src);
      if (w) attrs.width = w;
      video = isVideoSrc(attrs.src);
      attrs.src = withMediaToken(attrs.src);
    }
    if (video) {
      return ['video', mergeAttributes(attrs, { controls: 'true' })];
    }
    return ['img', attrs];
  },
});

export default MediaImage;
